import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import type { BuildingSummary, ClientDto } from "@klantpunt/shared";
import { api } from "./api";
import { useMe } from "./auth";
import { buildingIdFromPath, useBuildingContext, useBuildings } from "./buildingContext";

/**
 * De klant waarin je werkt (klantwisselaar, docs/CLIENTS.md §11). Per gebruiker en organisatie onthouden op dit
 * apparaat, ook offline. Null = "Alle klanten". Gebouwenlijst, gebouwkiezer, MJOP, keuringen, meterstanden en
 * taken tonen alleen gebouwen van de gekozen klant.
 */
const keyFor = (userId: string, orgId: string) => `kp-client-${userId}-${orgId}`;
const listeners = new Set<() => void>();
const memory = new Map<string, string | null>();
const read = (key: string | null) => {
  if (!key) return null;
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return memory.get(key) ?? null;
  }
};
function write(key: string, id: string | null) {
  memory.set(key, id);
  try {
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {
    /* geen opslag */
  }
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const clientsKey = (orgId: string | undefined) => ["clients", "active", orgId] as const;

export function useClients() {
  const { data: me } = useMe();
  return useQuery({
    queryKey: clientsKey(me?.org?.id),
    queryFn: () => api<ClientDto[]>("/clients"),
    enabled: Boolean(me?.org),
    staleTime: 60_000,
  });
}

export function useClientContext() {
  const { data: me } = useMe();
  const key = me?.org ? keyFor(me.user.id, me.org.id) : null;
  const stored = useSyncExternalStore(subscribe, () => read(key));
  const clients = useClients();
  // Een bewaarde klant die er niet (meer) is of die je niet ziet, telt niet.
  const client = clients.data?.find((c) => c.id === stored) ?? null;
  const id = clients.data ? (client?.id ?? null) : stored;
  const setClient = useCallback((next: string | null) => key && write(key, next), [key]);
  return { id, client, clients: clients.data, setClient };
}

/** Gebouwen van de gekozen klant (alle gebouwen bij "Alle klanten"). */
export function useClientBuildings(): { data: BuildingSummary[] | undefined; isPending: boolean } {
  const { id } = useClientContext();
  const q = useBuildings();
  return { data: id && q.data ? q.data.filter((b) => b.clientId === id) : q.data, isPending: q.isPending };
}

/** Filter voor lijsten met een gebouw-id: alleen gebouwen van de gekozen klant. */
export function useClientBuildingFilter(): (buildingId: string) => boolean {
  const { id } = useClientContext();
  const q = useBuildings();
  const ids = id && q.data ? new Set(q.data.filter((b) => b.clientId === id).map((b) => b.id)) : null;
  return (buildingId: string) => !ids || ids.has(buildingId);
}

/**
 * Houdt organisatie › klant › gebouw kloppend (één keer, in de Layout). Klant en gebouw worden los
 * onthouden; zonder deze controle kon na verversen een gebouw van een andere klant in de kiezer staan.
 * - Open je een gebouw van een andere klant (link, zoeken, melding, verversen op een gebouwpagina), dan
 *   volgt de klant het gebouw; zie je die klant niet in de wisselaar, dan wordt het "Alle klanten".
 * - Een onthouden gebouw (buiten een gebouwpagina) dat niet bij de gekozen klant hoort, laat je los.
 */
export function useKeepClientAndBuildingInStep() {
  const { id: clientId, clients, setClient } = useClientContext();
  const { building, setBuilding } = useBuildingContext();
  const { pathname } = useLocation();
  const fromPath = buildingIdFromPath(pathname);
  // De klant volgt het gebouw één keer per geopend pad. Kies je daarna zelf een klant (de wisselaar
  // navigeert dan weg van het gebouw), dan gaat die keuze voor.
  const last = useRef<{ path: string | null; client: string | null }>({ path: null, client: null });
  const due = useRef(false);

  useEffect(() => {
    if (last.current.path !== pathname) due.current = Boolean(fromPath);
    else if (last.current.client !== clientId) due.current = false;
    last.current = { path: pathname, client: clientId };
    if (!clients || !building) return;
    if (fromPath) {
      if (building.id !== fromPath || !due.current) return;
      due.current = false;
      if (clientId && building.clientId !== clientId)
        setClient(clients.some((c) => c.id === building.clientId) ? building.clientId : null);
    } else if (clientId && building.clientId !== clientId) {
      setBuilding(null);
    }
  }, [clientId, clients, building, fromPath, pathname, setClient, setBuilding]);
}
